import { onMounted, onBeforeUnmount, watch } from 'vue';

const MIN_INPUT_HEIGHT = 44;
const MAX_INPUT_HEIGHT = 120;
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Fixed controls must fit the visible area, not the larger layout viewport
// left behind by a mobile keyboard or pinch zoom. Keep the user's zoom intact.
export function getChatViewportMetrics(viewport, layoutWidth, layoutHeight) {
  const fullWidth = Math.max(1, finite(layoutWidth, 1));
  const fullHeight = Math.max(0, finite(layoutHeight, 0));
  const requestedWidth = finite(viewport?.width, fullWidth);
  const requestedHeight = finite(viewport?.height, fullHeight);
  const width = requestedWidth > 0 ? Math.min(requestedWidth, fullWidth) : fullWidth;
  const height = requestedHeight > 0 ? Math.min(requestedHeight, fullHeight) : fullHeight;
  const left = clamp(finite(viewport?.offsetLeft, 0), 0, fullWidth - width);
  const top = clamp(finite(viewport?.offsetTop, 0), 0, fullHeight - height);
  return { width, center: left + width / 2, bottom: fullHeight - height - top };
}

export function resizeChatTextarea(input, maximumHeight = MAX_INPUT_HEIGHT) {
  if (!input) return 0;
  const maxHeight = clamp(finite(maximumHeight, MAX_INPUT_HEIGHT), MIN_INPUT_HEIGHT, MAX_INPUT_HEIGHT);
  input.style.maxHeight = `${maxHeight}px`;
  input.style.height = `${MIN_INPUT_HEIGHT}px`;
  input.style.overflowY = 'hidden';
  // scrollHeight excludes borders, whereas the textarea uses border-box.
  const borders = Math.max(0, input.offsetHeight - input.clientHeight);
  const contentHeight = input.scrollHeight + borders;
  const height = clamp(contentHeight, MIN_INPUT_HEIGHT, maxHeight);
  input.style.height = `${height}px`;
  input.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  return height;
}

export function useChatComposer({ pageRef, composerRef, inputRef, draft }) {
  let mounted = false;
  let frame = null;
  let observer = null;
  let viewport = null;

  const setVariable = (name, value) => {
    const style = pageRef.value?.style;
    const pixels = `${value}px`;
    if (style && style.getPropertyValue(name) !== pixels) style.setProperty(name, pixels);
  };

  const resizeComposer = () => {
    if (!mounted || !pageRef.value) return;
    const metrics = getChatViewportMetrics(viewport, window.innerWidth, window.innerHeight);
    setVariable('--chat-viewport-width', metrics.width);
    setVariable('--chat-viewport-center', metrics.center);
    setVariable('--chat-viewport-bottom', metrics.bottom);
    // At high zoom, give the text a whole row instead of squeezing it
    // between the two fixed-size touch targets.
    pageRef.value.classList.toggle('chat-composer-stacked', metrics.width < 300);
    // Leave room for messages even with the keyboard open in landscape.
    const visibleHeight = finite(viewport?.height, window.innerHeight);
    resizeChatTextarea(inputRef.value, Math.max(MIN_INPUT_HEIGHT, visibleHeight * 0.4));
    // offsetHeight stays in CSS pixels even when the user zooms the page.
    setVariable('--chat-composer-height', composerRef.value?.offsetHeight || 0);
  };

  const scheduleResize = () => {
    if (!mounted || frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      resizeComposer();
    });
  };

  const observeComposer = () => {
    if (!mounted) return;
    observer?.disconnect();
    if (composerRef.value) observer?.observe(composerRef.value);
    // Upload permissions can add/remove the attachment button after loading,
    // changing the textarea width without changing the composer's outer size.
    if (inputRef.value) observer?.observe(inputRef.value);
    resizeComposer();
  };

  // Nutritional conversations can remove/recreate the composer with v-if.
  const stopRefs = watch([pageRef, composerRef, inputRef], observeComposer, { flush: 'post' });
  const stopDraft = watch(draft, resizeComposer, { flush: 'post' });

  onMounted(() => {
    mounted = true;
    viewport = window.visualViewport || null;
    window.addEventListener('resize', scheduleResize);
    window.addEventListener('orientationchange', scheduleResize);
    viewport?.addEventListener('resize', scheduleResize);
    viewport?.addEventListener('scroll', scheduleResize);
    if (typeof ResizeObserver !== 'undefined') observer = new ResizeObserver(scheduleResize);
    observeComposer();
  });

  onBeforeUnmount(() => {
    mounted = false;
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = null;
    observer?.disconnect();
    window.removeEventListener('resize', scheduleResize);
    window.removeEventListener('orientationchange', scheduleResize);
    viewport?.removeEventListener('resize', scheduleResize);
    viewport?.removeEventListener('scroll', scheduleResize);
    stopRefs();
    stopDraft();
  });

  return { resizeComposer };
}
